/**
 * `SCR-CLIENTS` (4 states), `SCR-CLIENT` (6), `SCR-MEASNEW` (10).
 * FLOWS_SPEC §10.6. OWNER: web-frontend-engineer.
 *
 * `ST-CLIENT-RECORDVIEW` renders `MeasurementRecordView` — THE CLIENT'S
 * READ-ONLY COMPONENT — never the authoring layout. `SCR-MEASNEW` renders
 * `MeasurementAuthoringForm`, which lives in its own module and shares no
 * layout with the record (RULING-001).
 */

import { useState } from 'react';
import { t } from '@pt/copy';
import { api } from '../../lib/api.ts';
import { useResource, useSubmission } from '../../lib/resource.ts';
import type { AtelierClientRow } from '../../lib/contracts.ts';
import { dateInWords, shortHash } from '@pt/view';
import { AppShell } from '../../app/AppShell.tsx';
import { useNavigation } from '../../app/Router.tsx';
import { useSession } from '../../app/SessionProvider.tsx';
import { BackOfficeTable, SearchField, type Column } from '../../components/backoffice.tsx';
import { MeasurementRecordView } from '../../components/record.tsx';
import { MeasurementAuthoringForm } from '../../components/measurementAuthoring.tsx';
import { PhotographStrip } from '../../components/photographs.tsx';
import { Button, FactLine, FactPair, Link, Statement, TextInput } from '../../components/primitives.tsx';
import { RefusalStatement } from '../../components/backoffice.tsx';
import { FetchError, LoadingRegion, OfflineStatement, Refused } from '../../components/states.tsx';
import type { ClientRecordEvent, PhotographSet } from '../../lib/contracts.ts';
import { stageLabel } from '../../components/order.tsx';

export function ClientsScreen() {
  const { navigate } = useNavigation();
  const [search, setSearch] = useState('');
  const clients = useResource(() => api.atelierClients({}), []);
  const rows = (clients.data?.clients ?? []).filter(
    (row) =>
      search === '' ||
      row.displayName.toLowerCase().includes(search.toLowerCase()) ||
      row.clientRef.toLowerCase().includes(search.toLowerCase()),
  );

  const columns: readonly Column<AtelierClientRow>[] = [
    { id: 'name', headingKey: 'clients.column.name', render: (row) => row.displayName },
    {
      id: 'ref',
      headingKey: 'clients.column.ref',
      render: (row) => <span className="pt-identifier">{row.clientRef}</span>,
    },
    {
      id: 'last',
      headingKey: 'clients.column.lastOrder',
      render: (row) => (row.lastOrderAt === null ? t('queue.nodate') : dateInWords(row.lastOrderAt)),
    },
    {
      id: 'count',
      headingKey: 'clients.column.orderCount',
      numeric: true,
      render: (row) => String(row.orderCount),
    },
  ];

  return (
    <AppShell title={t('clients.heading')}>
      {clients.phase === 'skeleton' || clients.phase === 'idle' ? (
        <LoadingRegion rows={8} variant="table" slow={clients.slow} label={t('clients.heading')} />
      ) : null}
      {clients.phase === 'error' ? <FetchError onRetry={clients.reload} /> : null}

      {clients.phase !== 'ready' ? null : rows.length === 0 && search === '' ? (
        <Statement>{t('clients.empty')}</Statement>
      ) : (
        <>
          {/* The construction the cloth list shares (backoffice.tsx, C3). */}
          <SearchField
            id="pt-client-search"
            label={t('clients.search.label')}
            value={search}
            onChange={setSearch}
          />
          <BackOfficeTable
            caption={t('clients.heading')}
            columns={columns}
            rows={rows}
            rowKey={(row) => row.clientId}
            rowHref={(row) => `/atelier/clients/${row.clientId}`}
            onOpenRow={(row) => navigate(`/atelier/clients/${row.clientId}`)}
          />
        </>
      )}

      {/* P-CLIENT-01. Until tonight there was no way to make one of these at
          all — every client in the store was put there by the seed. It stands
          under the list rather than over it, because the common act on this
          screen is finding somebody, not inventing one. */}
      {clients.phase !== 'ready' ? null : (
        <div className="pt-actions">
          <Button variant="secondary" onClick={() => navigate('/atelier/clients/new')}>
            {t('clientnew.action')}
          </Button>
        </div>
      )}
    </AppShell>
  );
}

/**
 * The photographs on file, as the client screen shows them: who took them and
 * whether they govern, the strip itself (components/photographs.tsx, shared
 * with the cut tickets), and OR-2's note.
 */
function PhotographsOnFile({ set, onTake }: { set: PhotographSet | null; onTake: () => void }) {
  return (
    <section className="pt-block">
      <h2 className="pt-cutsheet__blockTitle">{t('client.photographs.heading')}</h2>
      {set === null ? (
        <Statement>{t('client.photographs.empty')}</Statement>
      ) : (
        <>
          <FactLine>
            {t('client.photographs.taken', {
              at: dateInWords(set.takenAt),
              authorName: set.takenByName,
            })}
          </FactLine>
          {/* OR-2 made visible: this is the set the coat is cut to, or one kept
              to compare against. A cutter must never work that out from a date. */}
          <FactLine>
            {set.governs ? t('client.photographs.governs') : t('client.photographs.superseded')}
          </FactLine>
          <PhotographStrip photographs={set.photographs} />
          {/* The rule OR-2 confirmed, carried by the server and rendered here
              rather than re-typed: no measurement is ever taken from these. */}
          <FactLine>{set.measurementNote}</FactLine>
        </>
      )}
      <div className="pt-actions">
        <Button variant="secondary" onClick={onTake}>
          {t('client.photographs.take.action')}
        </Button>
      </div>
    </section>
  );
}

/** One line of the ledger, in words rather than in column names. */
function historyLine(entry: ClientRecordEvent): string {
  const at = dateInWords(entry.at);
  if (entry.event === 'client.created') {
    return t('client.history.created', { authorName: entry.actorDisplayName, at });
  }
  if (entry.event === 'client.note_added') {
    return t('client.history.note', { authorName: entry.actorDisplayName, at });
  }
  const fieldName =
    entry.field === 'display_name'
      ? t('client.amend.field.displayName')
      : entry.field === 'delivery_address'
        ? t('client.amend.field.deliveryAddress')
        : t('client.amend.field.contact');
  return t('client.history.amended', { authorName: entry.actorDisplayName, at, fieldName });
}

export function ClientScreen({ clientId }: { clientId: string }) {
  const { navigate } = useNavigation();
  const { session } = useSession();
  const detail = useResource(() => api.atelierClient(clientId), [clientId]);
  // The client's detail and his measurement history are two endpoints, because
  // opening the history is an AUDITED READ in its own right (D-016, [T-78]).
  const history = useResource(() => api.atelierMeasurementRecords(clientId), [clientId]);
  const [openRecordId, setOpenRecordId] = useState<string | null>(null);
  const noteSubmission = useSubmission();
  const amendSubmission = useSubmission();
  const [noteBody, setNoteBody] = useState('');
  const [amendField, setAmendField] = useState<'displayName' | 'deliveryAddress' | 'contact'>(
    'deliveryAddress',
  );
  const [amendValue, setAmendValue] = useState('');
  const [amendReason, setAmendReason] = useState('');
  const [amendedBy, setAmendedBy] = useState<string | null>(null);
  const data = detail.data;
  const records = history.data?.records ?? [];
  const record =
    openRecordId === null ? null : (records.find((r) => r.recordId === openRecordId) ?? null);

  const addNote = () =>
    void noteSubmission.run(async () => {
      const written = await api.addClientNote(clientId, { body: noteBody.trim() });
      setNoteBody('');
      detail.reload();
      return written;
    });

  const amend = () =>
    void amendSubmission.run(async () => {
      const written = await api.amendClient(clientId, {
        field: amendField,
        value: amendValue.trim(),
        ...(amendReason.trim() === '' ? {} : { reason: amendReason.trim() }),
      });
      setAmendValue('');
      setAmendReason('');
      setAmendedBy(written.amendedByName);
      detail.reload();
      return written;
    });

  return (
    <AppShell title={data?.client.displayName ?? t('global.nav.clients')}>
      {detail.phase === 'skeleton' || detail.phase === 'idle' ? (
        <LoadingRegion rows={8} variant="ledger" slow={detail.slow} label={t('global.nav.clients')} />
      ) : null}
      {detail.phase === 'error' ? <FetchError onRetry={detail.reload} /> : null}

      {data === null ? null : (
        <>
          {/* ── his details, and what the record does NOT claim ─────────── */}
          <section className="pt-block">
            <h2 className="pt-cutsheet__blockTitle">{t('client.details.heading')}</h2>
            <FactPair label={t('client.details.ref')}>
              <span className="pt-identifier">{data.client.clientRef}</span>
            </FactPair>
            <FactPair label={t('client.details.address')}>
              {data.client.deliveryAddress ?? t('client.details.none')}
            </FactPair>
            <FactPair label={t('client.details.contact')}>
              {data.client.contact ?? t('client.details.none')}
            </FactPair>
            {/* P-CLIENT-01. Derived from where the row came from, not hard-coded
                — a screen that calls a real client's name a placeholder is a
                screen a tailor learns not to believe. */}
            {data.client.displayNameIsPlaceholder ? (
              <FactLine>{t('client.details.placeholder')}</FactLine>
            ) : null}
            {data.client.hasPortalIdentifier === false ? (
              <FactLine>{t('client.details.noportal')}</FactLine>
            ) : null}
          </section>

          {/* ── the photographs (P-PHOTO-01, OR-2) ──────────────────────── */}
          <PhotographsOnFile
            set={data.photographs ?? null}
            onTake={() => navigate(`/atelier/clients/${clientId}/photographs/new`)}
          />

          <section className="pt-block">
            <h2 className="pt-cutsheet__blockTitle">{t('client.records.heading')}</h2>
            {/* Every open of the measurement history is audit-logged, AND THE
                PAGE SAYS SO ([T-78]). */}
            <FactLine>{t('client.records.audited')}</FactLine>
            {records.length === 0 ? (
              // Never an invitation to enter numbers on this screen; authoring
              // is its own screen and its own act.
              <Statement>{t('client.norecord')}</Statement>
            ) : (
              <ul>
                {records.map((entry) => (
                  <li key={entry.recordId} className="pt-row">
                    <Link
                      href={`#record-${entry.recordId}`}
                      onNavigate={() => setOpenRecordId(entry.recordId)}
                    >
                      {t('client.records.entry', {
                        takenAtDate: dateInWords(entry.takenAt),
                        authorName: entry.authoredByName,
                        contentHashShort: shortHash(entry.contentHash),
                        reason: entry.reason,
                      })}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
            {session?.role === 'master_tailor' ? (
              <div className="pt-actions">
                <Button onClick={() => navigate(`/atelier/clients/${clientId}/records/new`)}>
                  {t('client.newRecord.action')}
                </Button>
              </div>
            ) : null}
          </section>

          {/* ST-CLIENT-RECORDVIEW — the client's read-only component. */}
          {record === null ? null : (
            <MeasurementRecordView
              record={record}
              clientName={data.client.displayName}
              tailorName={record.authoredByName}
              readerIsStaff
            />
          )}

          {/* ── notes: law 31 names them beside measurements and pictures ─ */}
          <section className="pt-block">
            <h2 className="pt-cutsheet__blockTitle">{t('client.notes.heading')}</h2>
            <Statement>{t('client.notes.lede')}</Statement>
            <FactLine>{t('client.notes.internal')}</FactLine>
            {(data.notes ?? []).length === 0 ? (
              <Statement>{t('client.notes.empty')}</Statement>
            ) : (
              <ul>
                {(data.notes ?? []).map((note) => (
                  <li key={note.clientNoteId} className="pt-row">
                    <p>{note.body}</p>
                    <FactLine>
                      {t('client.notes.entry', {
                        authorName: note.authoredByName,
                        at: dateInWords(note.authoredAt),
                      })}
                    </FactLine>
                  </li>
                ))}
              </ul>
            )}
            {session?.role === 'master_tailor' || session?.role === 'admin' ? (
              <>
                <TextInput
                  id="pt-client-note"
                  label={t('client.notes.label')}
                  value={noteBody}
                  onChange={setNoteBody}
                  multiline
                  autoComplete="off"
                />
                {noteSubmission.rejection === null ? null : (
                  <RefusalStatement rejection={noteSubmission.rejection} />
                )}
                {noteSubmission.offline ? <OfflineStatement /> : null}
                <div className="pt-actions">
                  <Button variant="secondary" onClick={addNote} busy={noteSubmission.busy}>
                    {t('client.notes.submit')}
                  </Button>
                </div>
              </>
            ) : null}
          </section>

          {/* ── one change at a time, with what it was ──────────────────── */}
          {session?.role === 'master_tailor' || session?.role === 'admin' ? (
            <section className="pt-block">
              <h2 className="pt-cutsheet__blockTitle">{t('client.amend.heading')}</h2>
              <Statement>{t('client.amend.lede')}</Statement>
              <div className="pt-input">
                <label className="pt-input__label" htmlFor="pt-client-amend-field">
                  {t('client.amend.field.label')}
                </label>
                <select
                  id="pt-client-amend-field"
                  className="pt-input__field"
                  value={amendField}
                  onChange={(event) =>
                    setAmendField(event.target.value as 'displayName' | 'deliveryAddress' | 'contact')
                  }
                >
                  <option value="displayName">{t('client.amend.field.displayName')}</option>
                  <option value="deliveryAddress">{t('client.amend.field.deliveryAddress')}</option>
                  <option value="contact">{t('client.amend.field.contact')}</option>
                </select>
              </div>
              <TextInput
                id="pt-client-amend-value"
                label={t('client.amend.value.label')}
                value={amendValue}
                onChange={setAmendValue}
                multiline={amendField === 'deliveryAddress'}
                autoComplete="off"
              />
              {/* AN EMPTY BOX IS A REAL ACT HERE, so it says so before he taps.
                  The server accepts a blank value and clears the field — which
                  is right, because "he does not use that number any more" is a
                  correction a record must be able to hold. What is NOT right is
                  a tailor who has not finished typing tapping the button and
                  reading "Recorded by Paul" over a phone number he just wiped.
                  So the consequence is named at the point of the act, and the
                  action is not taken away: the ledger below keeps the old
                  value either way, which is what makes this recoverable. */}
              {amendValue.trim() === '' ? (
                <FactLine>
                  {t('client.amend.clearing', {
                    fieldName: t(`client.amend.field.${amendField}`),
                  })}
                </FactLine>
              ) : null}
              <TextInput
                id="pt-client-amend-reason"
                label={t('client.amend.reason.label')}
                value={amendReason}
                onChange={setAmendReason}
                autoComplete="off"
              />
              {amendSubmission.rejection === null ? null : (
                <RefusalStatement rejection={amendSubmission.rejection} />
              )}
              {amendSubmission.offline ? <OfflineStatement /> : null}
              {amendedBy === null ? null : (
                <Statement>{t('client.amend.done', { authorName: amendedBy })}</Statement>
              )}
              <div className="pt-actions">
                <Button variant="secondary" onClick={amend} busy={amendSubmission.busy}>
                  {t('client.amend.submit')}
                </Button>
              </div>
            </section>
          ) : null}

          {/* ── every value the record has held ─────────────────────────── */}
          <section className="pt-block">
            <h2 className="pt-cutsheet__blockTitle">{t('client.history.heading')}</h2>
            {(data.history ?? []).length === 0 ? (
              <Statement>{t('client.history.empty')}</Statement>
            ) : (
              <ul>
                {[...(data.history ?? [])].reverse().map((entry) => (
                  <li key={entry.seq} className="pt-row">
                    <FactLine>{historyLine(entry)}</FactLine>
                    {entry.oldValue === null && entry.newValue === null ? null : (
                      <p className="pt-history__values">
                        {`${t('client.history.from')} ${entry.oldValue ?? t('client.details.none')} · ` +
                          `${t('client.history.to')} ${entry.newValue ?? t('client.details.none')}`}
                      </p>
                    )}
                    {entry.reason === null ? null : <p>{entry.reason}</p>}
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="pt-section">
            <h2 className="pt-cutsheet__blockTitle">{t('client.orders.heading')}</h2>
            {data.orders.length === 0 ? (
              <Statement>{t('client.noorders')}</Statement>
            ) : (
              <ul>
                {data.orders.map((order) => (
                  <li key={order.orderRef} className="pt-row">
                    <Link href={`/atelier/orders/${order.orderRef}`} onNavigate={navigate}>
                      {`${order.orderRef} · ${stageLabel(order.state)}`}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      )}
    </AppShell>
  );
}

/** `SCR-MEASNEW`. MASTER TAILOR ONLY — any other role gets `ST-403`. */
export function MeasurementAuthoringScreen({ clientId }: { clientId: string }) {
  const { navigate } = useNavigation();
  const { session } = useSession();
  const submission = useSubmission();
  const detail = useResource(() => api.atelierClient(clientId), [clientId]);
  const history = useResource(() => api.atelierMeasurementRecords(clientId), [clientId]);
  const [draftSaved, setDraftSaved] = useState(false);
  const [lockedAt, setLockedAt] = useState<string | null>(null);

  if (session !== null && session.role !== 'master_tailor') {
    return (
      <AppShell title={t('e403.title')}>
        <Statement>{t('measnew.forbidden.statement')}</Statement>
        <Refused kind={403} />
      </AppShell>
    );
  }

  const data = detail.data;
  const current = (history.data?.records ?? []).find(
    (record) => record.status === 'locked' && record.supersededBy === null,
  );

  return (
    <AppShell title={t('client.newRecord.action')}>
      {detail.phase === 'skeleton' || detail.phase === 'idle' ? (
        <LoadingRegion rows={10} variant="ledger" slow={detail.slow} label={t('client.newRecord.action')} />
      ) : null}

      {lockedAt !== null ? (
        <Statement>
          {current === undefined
            ? t('measnew.locked.initial', {
                lockedAt: dateInWords(lockedAt),
                clientNameStaff: data?.client.displayName ?? '',
              })
            : t('measnew.locked.statement', {
                lockedAt: dateInWords(lockedAt),
                previousTakenAt: dateInWords(current.takenAt),
              })}
        </Statement>
      ) : data === null ? null : (
        <MeasurementAuthoringForm
          clientName={data.client.displayName}
          supersedes={current?.recordId ?? null}
          previousTakenAt={current === undefined ? null : dateInWords(current.takenAt)}
          busy={submission.busy}
          draftSaved={draftSaved}
          refusal={
            submission.rejection === null ? null : <FetchError whatKey="measnew.error.means" />
          }
          onSaveDraft={(payload) => {
            void submission
              .run(() => api.createMeasurementRecord(clientId, payload))
              .then((result) => result && setDraftSaved(true));
          }}
          onLock={(payload) => {
            void submission
              .run(async () => {
                const created = await api.createMeasurementRecord(clientId, payload);
                return api.lockMeasurementRecord(created.recordId);
              })
              .then((result) => {
                if (result === null) return;
                setLockedAt(result.lockedAt);
              });
          }}
        />
      )}

      <div className="pt-actions">
        <Button onClick={() => navigate(`/atelier/clients/${clientId}`)}>
          {t('global.action.back')}
        </Button>
      </div>
    </AppShell>
  );
}
